import {
  RegionalHighlightPurchaseOrderStatus,
  type RegionalHighlightPurchaseDurationMonths,
  type RegionalHighlightPurchaseOrder,
  type RegionalHighlightPurchaseOrdersResponse,
} from '@/types/regionalHighlight';

type RecordValue = Record<string, unknown>;

const asRecord = (value: unknown): RecordValue | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const normalizeDurationMonths = (
  value: unknown,
): RegionalHighlightPurchaseDurationMonths | undefined =>
  value === 3 || value === 6 || value === 12 ? value : undefined;

const normalizeStatus = (value: unknown): RegionalHighlightPurchaseOrderStatus | undefined =>
  Object.values(RegionalHighlightPurchaseOrderStatus).includes(
    value as RegionalHighlightPurchaseOrderStatus,
  )
    ? (value as RegionalHighlightPurchaseOrderStatus)
    : undefined;

const normalizeOrder = (value: unknown): RegionalHighlightPurchaseOrder | undefined => {
  const raw = asRecord(value);
  const orderId = asString(raw?.orderId);
  const districtId = asString(raw?.districtId);
  const requestedAt = asString(raw?.requestedAt);
  const status = normalizeStatus(raw?.status);
  const durationMonths = normalizeDurationMonths(raw?.durationMonths);

  if (!raw || !orderId || !districtId || !requestedAt || !status || durationMonths === undefined) {
    return undefined;
  }

  return {
    orderId,
    status,
    districtId,
    districtLabel: asString(raw.districtLabel),
    durationMonths,
    productCode: asString(raw.productCode),
    monthlyPriceChfCents: asFiniteNumber(raw.monthlyPriceChfCents),
    termTotalChfCents: asFiniteNumber(raw.termTotalChfCents),
    listingId: asString(raw.listingId),
    locale: asString(raw.locale),
    requestedAt,
    renewSlotId: asString(raw.renewSlotId),
  };
};

const ordersFrom = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  const raw = asRecord(value);
  if (raw && Array.isArray(raw.orders)) return raw.orders;
  return [];
};

export const normalizeRegionalHighlightPurchaseOrder = (
  value: unknown,
): RegionalHighlightPurchaseOrder | undefined => normalizeOrder(value);

export const normalizeRegionalHighlightPurchaseOrdersResponse = (
  value: unknown,
): RegionalHighlightPurchaseOrdersResponse => ({
  orders: ordersFrom(value)
    .map(normalizeOrder)
    .filter((order): order is RegionalHighlightPurchaseOrder => Boolean(order)),
});
